management:
  enquiries:
    description: "When you receive a Yacht Enquiry, an email will be sent to the following recipients:"
    addRecipient: Add Recipient
    actions:
      add: Add Enquiry Recipient
      update: Update Enquiry Recipient
      delete: Remove Enquiry Recipient

yachtFilter:
  title: Yacht Filters
  length:
    title: Length
    value:
    description:
      none: You are not excluding any yachts by length.
      range: Yachts outside of this range will not appear on your API service.
      min: Shorter yachts will not appear on your API service.
      max: Longer yachts will not appear on your API service.

excludedYachts:
  title: Hidden Yachts
  count_zero: You have not hidden any yachts.
  count_one: You have hidden 1 yacht.
  count_other: You have hidden {{ count }} yachts.
  description: Hidden yachts will not be shown in your yacht searches.
  add: Add Hidden Yacht

featuredYachts:
  title: Featured Yachts
  count_zero: You have not featured any yachts.
  count_one: You have featured 1 yacht.
  count_other: You have featured {{ count }} yachts.
  description: Featured yachts will appear in your 'featured' yacht searches.
  add: Add Featured Yacht

featuredFleets:
  title: Featured Fleets
  count_zero: You have not featured any central agencies' fleets.
  count_one: You have featured 1 central agency's fleet.
  count_other: You have featured {{ count }} central agencies' fleets.
  description: Featured fleets will appear in your 'featured' yacht searches.
  add: Add Featured Fleet
  
excludedFleets:
  title: Hidden Fleets
  count_zero: You have not hidden any central agencies' fleets.
  count_one: You have hidden 1 central agency's fleet.
  count_other: You have hidden {{ count }} central agencies' fleets.
  description: Agencies that you would rather not work with can be hidden from your API.
  add: Add Hidden Fleet

fleetAccess:
  title: Fleet Authorisation
  summary:
    explanation: Some Associations (MYBA, AYSS) have rules forbidding the marketing of a fleet without permission from the Central Agency.
    permissionBased:
      description: You are only showing Central Agent fleets which have authorised you to market their yachts.
      compliance: Your current settings comply with this requirement.
    unrestrictedAccess: 
      description: You are showing yachts from all Central Agent fleets.
      compliance: Your current settings do <strong>not</strong> comply with this requirement. If you are a member of one of these associations, you may need to change this setting.
  status:
    requestAccess: 
      title: Request Fleet Access
      description: |
        <p>You are requesting authorisation from {{ centralAgentCompany }} to market their fleet via your website.</p>
        <p>Once submitted, {{ centralAgentCompany }} will be notified and can choose to approve or decline your request. Please note that resubmission is not permitted for 30 days following a pending request.</p>
      contact:
        title: 💡 Want a faster approval?
        body: Central agents are far more likely to respond positively to a personal approach. Consider sending a brief, friendly email directly to {{ centralAgentCompany }} introducing yourself and letting them know you'd like to feature their fleet on your site — before submitting this request. A warm introduction goes a long way in this industry.
        action: View suggested email template
        suggestedEmail:
          subject: Featuring your fleet - {{ brokerCompany }}
          body: |
            Dear {{ centralAgentPrimaryContact }},

            I hope this message finds you well. My name is {{ brokerName }} and I am a yacht charter broker with {{ brokerCompany }}.

            I am writing to introduce myself and to ask whether you would be happy for us to feature the yachts you represent on our website. We work with a broad range of clients and would love to be able to present your fleet to them when relevant opportunities arise.

            We of course ensure that all enquiries are directed exclusively to you as central agent, and we would never seek to circumvent that relationship.

            If you are happy to grant permission, please do let me know and we will take care of the rest from our end.

            With kind regards,

            {{ brokerName }}

            {{ brokerCompany }}

            {{ brokerTelephone }}
        openDraft: Open Draft
      selfAuth:
        title: Already have permission?
        body: If you have already received permission from {{ centralAgentCompany }} to market their fleet, you can self-authorise directly without submitting this request.
        action: Self-Authorise Fleet
      accept: Request Fleet
      success: Access Request Sent
    requestPending:
      title: Awaiting Response
    requestAccepted:
      title: Permission Granted
    requestRejected:
      title: Request Rejected
    followUpByEmail: Follow up by <0>email</0>
  selfAuthorise:
    title: Confirm Existing Authorisation
    label: Already have permission?
    description: |
      <p>If you have already obtained permission from {{ companyName }} through private communication to market their fleet, you may self-authorise access now.</p>
      <p><strong>Important</strong>: This action cannot be reversed without Charter Index admin intervention. You will receive an email confirmation of this authorisation. Please ensure you have genuine authorisation from {{ companyName }} before proceeding, as any errors or unauthorised submissions will require manual correction by our team and may result in account restrictions.</p>
    confirm: I confirm that {{ companyName }} has given me permission to market their fleet
  list:
    authorisedDescription: This is a list of Central Agencies who <strong>have</strong> authorised you to market their fleets.
    unauthorisedDescription: This is a list of Central Agencies who have <strong>not</strong> authorised you to market their fleets.
    requestDescription: You can request marketing authorisation from any company that is currently blocking you once a month. It is recommended that you follow up with a personal email if your request is rejected or ignored.
  
distribution:
  title: Fleet Distribution
  mode:
    preAuthorised:
      title: Open Distribution
      description: Your fleet is pre-authorised for all charter brokerages. Brokers operating in either Unrestricted Access Mode or Permission-Based Mode will automatically receive your fleet data without needing to submit access requests.
      notification: Your fleet is now pre-authorised.
    restricted:
      title: Restrict distribution
      description: Your fleet is only available to brokers you explicitly authorise. Brokers must submit individual access requests to market your yachts, which you can approve or decline. Your fleet will not appear in any broker's API feed until you grant authorisation.
      legalWarning: |
        Restricting access to this data might be viewed as anti-competitive
        conduct, potentially stifling innovation, diminishing market transparency,
        and adversely affecting consumers. Such actions could also expose
        {{ companyName }} to legal scrutiny. It's advisable to consult with
        independent legal counsel.
      notificationWarning: |
        Subscribers to the Charter Index API service will be notified that this
        fleet restriction has been placed on their accounts.
      notification: Your fleet is now restricted.
  requests:
    title: Access Requests
    description: "You have received the following requests to distribute your fleet via the Charter Index API service:"
    accept: Approve
    reject: Deny
  authorised:
    title: Authorised Brokers
    description: "You have authorised the following brokers to market your fleet via the Charter Index API service:"
  confirmAuthorisation:
    title: Confirm API Fleet Authorisation
    body: |
      <p>You are granting {{ brokerName }} permission to access and market your fleet data via the Charter Index API.</p>
      <p><strong>Please note</strong>: This authorisation cannot be undone from your dashboard. Should you wish to revoke access in future, you will need to contact the Charter Index team directly to process this change.</p>
    checkbox: I understand this authorisation requires Charter Index assistance to revoke

iFrame:
  url: URL
  featuredUrl: Featured URL
  units: Units
  currency: Currency
  update:
    title: Update iFrame Settings
  summaryDescription: A summary of which yachts & fleets your iFrame will show.

api:
  username: Username
  password: Password
  authHeader: Authorization Header
  restUrl: REST URL
  graphQlUrl: GraphQL URL
